<template>
  <div class="flex flex-col gap-4 p-3">
    <div
      v-for="(alternative, index) in localAlternatives"
      :key="alternative._tempId"
      class="border border-gray-200 rounded-lg overflow-hidden"
    >
      <button
        type="button"
        class="w-full flex items-start gap-2 px-4 py-3 bg-gray-50 hover:bg-gray-100 text-left transition-colors"
        @click.stop="toggleExpand(index)"
      >
        <ChevronRightIcon
          class="w-4 h-4 text-gray-500 shrink-0 transition-transform duration-200 mt-0.5"
          :class="{ 'rotate-90': expandedIndex === index }"
        />
        <div class="flex flex-col flex-1">
          <span class="text-base font-medium text-gray-900">Alternative {{ index + 1 }}</span>
          <span
            v-if="alternative.description && expandedIndex !== index"
            class="text-sm text-gray-500 mt-1"
            >{{ alternative.description }}</span
          >
        </div>
        <button
          type="button"
          class="p-1 text-red-500 hover:text-red-700 disabled:opacity-50 shrink-0"
          :disabled="disabled || review.isReviewReadonly.value"
          @click.stop="removeAlternative(index)"
        >
          <XMarkIcon class="w-5 h-5" />
        </button>
      </button>

      <div v-if="expandedIndex === index" class="p-4">
        <div class="flex flex-col gap-3">
          <div class="flex flex-col gap-1">
            <label class="text-sm font-semibold text-gray-900">Name</label>
            <Combobox
              v-model="alternative.name"
              allow-create
              :items="review.medicalAlternativeNames.value"
              :display-fn="(i) => i"
              placeholder="Alternative name"
              :disabled="disabled || review.isReviewReadonly.value"
              :create-message="(v) => v"
              @update:model-value="syncToServer"
              @create="
                (v) => {
                  alternative.name = v;
                  syncToServer();
                }
              "
              @enter="syncToServer"
            />
          </div>

          <div class="flex flex-col gap-1">
            <label class="text-sm font-semibold text-gray-900">Description</label>
            <TextInput
              v-model="alternative.description"
              placeholder="Description (optional)"
              :disabled="disabled || review.isReviewReadonly.value"
              multiline
              input-classes="w-full"
              @blur="syncToServer"
              @enter="syncToServer"
            />
          </div>

          <div class="flex flex-col gap-2">
            <label class="text-sm font-semibold text-gray-900">Indications</label>
            <div class="flex flex-wrap gap-2 items-center">
              <div
                v-for="(indication, indicationIndex) in alternative.indications"
                :key="'indication-' + indicationIndex"
                class="flex items-center text-sm gap-1 px-4 py-2.5 rounded-full text-green-700 bg-green-100 font-medium"
              >
                <span
                  v-tooltip="indication.length > 35 ? indication : ''"
                  class="truncate max-w-[252px]"
                  >{{ indication }}</span
                >
                <button
                  v-if="!review.isReviewReadonly.value"
                  :disabled="disabled"
                  @click="removeFromArray(alternative.indications, indicationIndex)"
                >
                  <XMarkIcon class="w-4 h-4" />
                </button>
              </div>
              <FloatingTextInput
                v-if="!review.isReviewReadonly.value"
                :disabled="disabled"
                @save="addToArray(alternative.indications, $event)"
              >
                <template #activator="{ show }">
                  <button
                    class="flex items-center gap-1 text-sm border-2 py-1.5 px-3 rounded-full text-gray-700 border-gray-200 border-dashed font-medium"
                    :disabled="disabled"
                    @click.stop="show"
                  >
                    <PlusIcon class="w-4 h-4" /> Add indication
                  </button>
                </template>
              </FloatingTextInput>
            </div>
          </div>

          <div class="flex flex-col gap-2">
            <label class="text-sm font-semibold text-gray-900">Contraindications</label>
            <div class="flex flex-wrap gap-2 items-center">
              <div
                v-for="(contraindication, contraIndex) in alternative.contraindications"
                :key="'contra-' + contraIndex"
                class="flex items-center text-sm gap-1 px-4 py-2.5 rounded-full text-red-700 bg-red-100 font-medium"
              >
                <span
                  v-tooltip="contraindication.length > 35 ? contraindication : ''"
                  class="truncate max-w-[252px]"
                  >{{ contraindication }}</span
                >
                <button
                  v-if="!review.isReviewReadonly.value"
                  :disabled="disabled"
                  @click="removeFromArray(alternative.contraindications, contraIndex)"
                >
                  <XMarkIcon class="w-4 h-4" />
                </button>
              </div>
              <FloatingTextInput
                v-if="!review.isReviewReadonly.value"
                :disabled="disabled"
                @save="addToArray(alternative.contraindications, $event)"
              >
                <template #activator="{ show }">
                  <button
                    class="flex items-center gap-1 text-sm border-2 py-1.5 px-3 rounded-full text-gray-700 border-gray-200 border-dashed font-medium"
                    :disabled="disabled"
                    @click.stop="show"
                  >
                    <PlusIcon class="w-4 h-4" /> Add contraindication
                  </button>
                </template>
              </FloatingTextInput>
            </div>
          </div>

          <div class="flex flex-col gap-2">
            <label class="text-sm font-semibold text-gray-900">Target Population - Inclusion</label>
            <div class="flex flex-wrap gap-2 items-center">
              <div
                v-for="(inclusion, inclusionIndex) in alternative.targetPopulation.inclusion"
                :key="'inclusion-' + inclusionIndex"
                class="flex items-center text-sm gap-1 px-4 py-2.5 rounded-full text-green-700 bg-green-100 font-medium"
              >
                <span
                  v-tooltip="inclusion.length > 35 ? inclusion : ''"
                  class="truncate max-w-[252px]"
                  >{{ inclusion }}</span
                >
                <button
                  v-if="!review.isReviewReadonly.value"
                  :disabled="disabled"
                  @click="removeFromArray(alternative.targetPopulation.inclusion, inclusionIndex)"
                >
                  <XMarkIcon class="w-4 h-4" />
                </button>
              </div>
              <FloatingTextInput
                v-if="!review.isReviewReadonly.value"
                :disabled="disabled"
                @save="addToArray(alternative.targetPopulation.inclusion, $event)"
              >
                <template #activator="{ show }">
                  <button
                    class="flex items-center gap-1 text-sm border-2 py-1.5 px-3 rounded-full text-gray-700 border-gray-200 border-dashed font-medium"
                    :disabled="disabled"
                    @click.stop="show"
                  >
                    <PlusIcon class="w-4 h-4" /> Add target population
                  </button>
                </template>
              </FloatingTextInput>
            </div>
          </div>

          <div class="flex flex-col gap-2">
            <label class="text-sm font-semibold text-gray-900">Target Population - Exclusion</label>
            <div class="flex flex-wrap gap-2 items-center">
              <div
                v-for="(exclusion, exclusionIndex) in alternative.targetPopulation.exclusion"
                :key="'exclusion-' + exclusionIndex"
                class="flex items-center text-sm gap-1 px-4 py-2.5 rounded-full text-red-700 bg-red-100 font-medium"
              >
                <span
                  v-tooltip="exclusion.length > 35 ? exclusion : ''"
                  class="truncate max-w-[252px]"
                  >{{ exclusion }}</span
                >
                <button
                  v-if="!review.isReviewReadonly.value"
                  :disabled="disabled"
                  @click="removeFromArray(alternative.targetPopulation.exclusion, exclusionIndex)"
                >
                  <XMarkIcon class="w-4 h-4" />
                </button>
              </div>
              <FloatingTextInput
                v-if="!review.isReviewReadonly.value"
                :disabled="disabled"
                @save="addToArray(alternative.targetPopulation.exclusion, $event)"
              >
                <template #activator="{ show }">
                  <button
                    class="flex items-center gap-1 text-sm border-2 py-1.5 px-3 rounded-full text-gray-700 border-gray-200 border-dashed font-medium"
                    :disabled="disabled"
                    @click.stop="show"
                  >
                    <PlusIcon class="w-4 h-4" /> Add target population
                  </button>
                </template>
              </FloatingTextInput>
            </div>
          </div>

          <div class="mt-2 space-y-5">
            <div class="flex flex-col gap-2">
              <label class="text-sm font-semibold text-gray-900">
                Position in line of therapy <span class="text-red-500">*</span>
              </label>

              <div class="flex items-center gap-6">
                <label
                  v-for="option in positionInTherapyOptions"
                  :key="option.value"
                  class="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    class="w-4 h-4 rounded border-gray-300 accent-blue-600 focus:ring-blue-500"
                    :disabled="disabled || review.isReviewReadonly.value"
                  />
                  {{ option.label }}
                </label>
              </div>
            </div>
            <div class="flex flex-col gap-2">
              <label class="text-sm font-semibold text-gray-900">
                Benchmark authority<span
                  class="cursor-help font-normal text-gray-400"
                  title="Gold-standard: Widely accepted benchmark with the strongest evidence and clinical consensus. Indicates the best-established option, not novelty."
                >
                  ⓘ
                </span>
                <span class="text-red-500">*</span>
              </label>
              <div class="flex items-center gap-6">
                <label
                  v-for="option in benchmarkAuthorityOptions"
                  :key="option.value"
                  class="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    class="w-4 h-4 rounded border-gray-300 accent-blue-600 focus:ring-blue-500"
                    :disabled="disabled || review.isReviewReadonly.value"
                  />
                  {{ option.label }}
                </label>
              </div>
            </div>
            <div class="flex flex-col gap-2">
              <label class="text-sm font-semibold text-gray-900">
                Mode of use <span class="text-red-500">*</span>
              </label>

              <div class="flex items-center gap-6">
                <label
                  v-for="option in modeOfUseOptions"
                  :key="option.value"
                  class="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    class="w-4 h-4 rounded border-gray-300 accent-blue-600 focus:ring-blue-500"
                    :disabled="disabled || review.isReviewReadonly.value"
                  />
                  {{ option.label }}
                </label>
              </div>
            </div>
            <div class="flex flex-col gap-2">
              <label class="text-sm font-semibold text-gray-900">
                Innovation status
                <span
                  class="cursor-help font-normal text-gray-400"
                  title="First-in-class: Introduces a new mechanism or technology not previously available. Indicates novelty, not that it is the best or most established option."
                >
                  ⓘ
                </span>
                <span class="text-red-500">*</span>
              </label>

              <div class="flex items-center gap-6">
                <label
                  v-for="option in innovationStatusOptions"
                  :key="option.value"
                  class="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    class="w-4 h-4 rounded border-gray-300 accent-blue-600 focus:ring-blue-500"
                    :disabled="disabled || review.isReviewReadonly.value"
                  />
                  {{ option.label }}
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <button
      v-if="!review.isReviewReadonly.value"
      type="button"
      class="flex items-center gap-2 text-blue-800 disabled:opacity-50"
      :disabled="disabled"
      @click="addAlternative"
    >
      <PlusIcon class="w-5 h-5" />
      <span>Add alternative</span>
    </button>

    <div v-if="localAlternatives.length > 0" class="border-t pt-4 mt-2">
      <div class="flex items-center gap-3">
        <label class="text-sm font-medium text-gray-700">Overall Assessment</label>
        <label class="flex items-center gap-2 cursor-pointer">
          <input
            v-model="localEquipoise"
            type="checkbox"
            :disabled="disabled || review.isReviewReadonly.value"
            class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            @change="syncToServer"
          />
          <span class="text-sm text-gray-600"
            >Equipoise (alternatives are considered equivalent)</span
          >
        </label>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from "vue";
import { useDebounceFn } from "@vueuse/core";
import type {
  MedicalAlternative,
  MedicalAlternativesValue,
} from "@core/domain/types/attributeValue.type";
import {
  PositionInTherapy,
  BenchmarkAuthority,
  ModeOfUse,
  InnovationStatus,
} from "@core/domain/types/attributeValue.type";
import type { ReviewItem } from "@core/domain/models/reviewItem.model";
import type { AttributeStructure } from "@core/domain/models/data-extraction-plan-attribute";
import TextInput from "@app/components/Global/Inputs/TextInput.vue";
import FloatingTextInput from "@app/components/Global/Inputs/FloatingTextInput.vue";
import { XMarkIcon, PlusIcon, ChevronRightIcon } from "@heroicons/vue/24/solid";
import { injectStrict } from "@app/utils/injectStrict";
import { ReviewKey } from "../../../use-review";
import useSnackbar from "@app/composables/use-snackbar";
import { HttpException } from "@core/exceptions/http.exception";
import { SnackbarState } from "@app/types";
import { errorMessage } from "@app/utils/error-message";
import Combobox from "@app/components/Global/Inputs/Combobox.vue";

const positionInTherapyOptions = [
  { value: PositionInTherapy.FirstInLine, label: "First in line" },
  { value: PositionInTherapy.SecondInLine, label: "Second in line" },
  { value: PositionInTherapy.ThirdInLine, label: "Third in line" },
  { value: PositionInTherapy.NotReported, label: "Not reported" },
];

const benchmarkAuthorityOptions = [
  { value: BenchmarkAuthority.GoldStandard, label: "Gold standard" },
  { value: BenchmarkAuthority.StandardOfCare, label: "Standard of care" },
  { value: BenchmarkAuthority.NotEstablished, label: "Not established" },
  { value: BenchmarkAuthority.NotReported, label: "Not reported" },
];

const modeOfUseOptions = [
  { value: ModeOfUse.StandAlone, label: "Stand alone" },
  { value: ModeOfUse.Adjunctive, label: "Adjunctive" },
  { value: ModeOfUse.NotReported, label: "Not reported" },
];

const innovationStatusOptions = [
  { value: InnovationStatus.FirstInClass, label: "First in class" },
  { value: InnovationStatus.NotReported, label: "Not reported" },
];

const props = defineProps<{
  study: ReviewItem;
  attributeStructure: AttributeStructure;
  disabled?: boolean;
}>();

const review = injectStrict(ReviewKey);
const snackbar = useSnackbar();
const currentAttribute = computed(() =>
  props.study.synthesis?.attributes?.find(
    (attr) => attr.attributeStructureId === props.attributeStructure?.id,
  ),
);

let tempIdCounter = 0;

function normalizeAlternative(alt: any): MedicalAlternative & { _tempId: number } {
  return {
    ...alt,
    indications: alt.indications || [],
    contraindications: alt.contraindications || [],
    targetPopulation: {
      inclusion: alt.targetPopulation?.inclusion || [],
      exclusion: alt.targetPopulation?.exclusion || [],
    },
    assessment: alt.assessment || {
      positionInTherapy: undefined,
      benchmarkAuthority: undefined,
      modeOfUse: undefined,
      innovationStatus: undefined,
    },
    _tempId: tempIdCounter++,
  };
}

const localAlternatives = ref<Array<MedicalAlternative & { _tempId: number }>>([]);
const localEquipoise = ref(false);
const expandedIndex = ref<number | null>(null);

function toggleExpand(index: number) {
  expandedIndex.value = expandedIndex.value === index ? null : index;
}

let skipServerLoadUntil = 0;
const SKIP_DURATION = 3000;

function loadFromServer() {
  const attr = currentAttribute.value;
  if (attr) {
    const value = attr.value as MedicalAlternativesValue;
    localAlternatives.value = (value?.alternatives || []).map(normalizeAlternative);
    localEquipoise.value = value?.equipoise || false;
  } else {
    localAlternatives.value = [];
    localEquipoise.value = false;
  }
}

watch(
  () => props.study.id,
  () => {
    loadFromServer();
  },
  { immediate: true },
);

watch(
  () => props.attributeStructure?.id,
  () => {
    loadFromServer();
  },
);

watch(
  () => currentAttribute.value?.value,
  () => {
    if (Date.now() < skipServerLoadUntil) {
      return;
    }
    loadFromServer();
  },
  { deep: true },
);

function addAlternative() {
  localAlternatives.value.push(
    normalizeAlternative({
      name: "",
      description: "",
      indications: [],
      contraindications: [],
      targetPopulation: { inclusion: [], exclusion: [] },
      assessment: {
        positionInTherapy: undefined,
        benchmarkAuthority: undefined,
        modeOfUse: undefined,
        innovationStatus: undefined,
      },
    }),
  );
  expandedIndex.value = localAlternatives.value.length - 1;
}

function addToArray(array: string[] | undefined, value: string) {
  if (!value.trim()) return;
  if (array) {
    array.push(value.trim());
  }
  syncToServer();
}

function removeFromArray(array: string[] | undefined, index: number) {
  if (array) {
    array.splice(index, 1);
  }
  syncToServer();
}

function removeAlternative(index: number) {
  localAlternatives.value.splice(index, 1);
  syncToServer();
}

function syncToServer() {
  const cleanedAlternatives = localAlternatives.value.map((alt) => {
    const { _tempId, ...rest } = alt;
    return rest;
  });

  debouncedSave({
    alternatives: cleanedAlternatives,
    equipoise: localEquipoise.value,
  });
}

const debouncedSave = useDebounceFn(async (value: MedicalAlternativesValue) => {
  try {
    skipServerLoadUntil = Date.now() + SKIP_DURATION;
    await review.saveMedicalAlternativesAttributeValue(
      props.study.id,
      props.attributeStructure.id,
      value,
    );
    skipServerLoadUntil = Date.now() + SKIP_DURATION;
  } catch (e) {
    const error = e as HttpException;
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data));
  }
}, 1000);
</script>
